import React, { useState, useRef, useEffect } from 'react';

function AIChat({ conversations, activeConv, selectedModel, models, onSend, onNewChat, onSelectConv, onDeleteConv, onChangeModel }) {
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [showSidebar, setShowSidebar] = useState(true);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [activeConv?.messages]);

  useEffect(() => {
    const lastMsg = activeConv?.messages[activeConv.messages.length - 1];
    if (lastMsg?.role === 'user') {
      setIsTyping(true);
      const timer = setTimeout(() => setIsTyping(false), 1500 + Math.random() * 2000);
      return () => clearTimeout(timer);
    }
  }, [activeConv?.messages]);

  const handleSend = () => {
    if (!input.trim()) return;
    onSend(input.trim());
    setInput('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const modelInfo = models.find((m) => m.id === selectedModel);

  const renderMarkdown = (text) => {
    // Simple markdown rendering
    let html = text
      .replace(/```(\w*)\n([\s\S]*?)```/g, '<pre><code>$2</code></pre>')
      .replace(/`([^`]+)`/g, '<code class="ai-inline-code">$1</code>')
      .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
      .replace(/\*([^*]+)\*/g, '<em>$1</em>')
      .replace(/^## (.+)$/gm, '<h3 class="ai-md-h3">$1</h3>')
      .replace(/^# (.+)$/gm, '<h2 class="ai-md-h2">$1</h2>')
      .replace(/^### (.+)$/gm, '<h4 class="ai-md-h4">$1</h4>')
      .replace(/^- (.+)$/gm, '<li>$1</li>')
      .replace(/^> (.+)$/gm, '<blockquote class="ai-blockquote">$1</blockquote>')
      .replace(/\n/g, '<br/>');

    return <div className="ai-msg-content" dangerouslySetInnerHTML={{ __html: html }} />;
  };

  return (
    <div className="ai-chat-layout">
      {/* Sidebar */}
      <div className={`ai-chat-sidebar ${showSidebar ? 'open' : ''}`}>
        <div className="ai-chat-sidebar-header">
          <button className="ai-new-chat-btn" onClick={onNewChat}>
            + New Chat
          </button>
        </div>
        <div className="ai-chat-sidebar-list">
          {conversations.map((conv) => (
            <div
              key={conv.id}
              className={`ai-conv-item ${conv.id === activeConv?.id ? 'active' : ''}`}
              onClick={() => onSelectConv(conv.id)}
            >
              <span className="ai-conv-icon">💬</span>
              <span className="ai-conv-title">{conv.title}</span>
              <span className="ai-conv-count">{conv.messages.length}</span>
              <button
                className="ai-conv-delete"
                onClick={(e) => { e.stopPropagation(); onDeleteConv(conv.id); }}
              >
                ×
              </button>
            </div>
          ))}
        </div>
        <div className="ai-chat-sidebar-footer">
          <select
            className="ai-model-select"
            value={selectedModel}
            onChange={(e) => onChangeModel(e.target.value)}
          >
            {models.map((m) => (
              <option key={m.id} value={m.id}>{m.icon} {m.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Chat Area */}
      <div className="ai-chat-main">
        <div className="ai-chat-header">
          <button className="ai-sidebar-toggle" onClick={() => setShowSidebar(!showSidebar)}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="3" width="18" height="18" rx="2" /><line x1="9" y1="3" x2="9" y2="21" />
            </svg>
          </button>
          <div className="ai-chat-header-info">
            <span className="ai-chat-header-title">{activeConv?.title || 'New Chat'}</span>
            <span className="ai-chat-header-model" style={{ color: modelInfo?.color }}>
              {modelInfo?.icon} {modelInfo?.name}
            </span>
          </div>
        </div>

        <div className="ai-chat-messages">
          {activeConv?.messages.length === 0 && (
            <div className="ai-chat-empty">
              <div className="ai-chat-empty-icon">🤖</div>
              <h3>How can I help you today?</h3>
              <p>Ask me anything — code, analysis, writing, brainstorming, and more.</p>
              <div className="ai-chat-suggestions">
                {['Explain React hooks', 'Write a SQL query', 'Debug my code', 'Create a marketing plan'].map((s) => (
                  <button key={s} className="ai-suggestion-btn" onClick={() => { setInput(s); inputRef.current?.focus(); }}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {activeConv?.messages.map((msg) => (
            <div key={msg.id} className={`ai-chat-msg ${msg.role}`}>
              <div className="ai-chat-msg-avatar">
                {msg.role === 'user' ? '🧑‍💻' : modelInfo?.icon || '🤖'}
              </div>
              <div className="ai-chat-msg-body">
                <div className="ai-chat-msg-header">
                  <span className="ai-chat-msg-name">
                    {msg.role === 'user' ? 'You' : modelInfo?.name || 'AI'}
                  </span>
                  <span className="ai-chat-msg-time">
                    {new Date(msg.timestamp).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                  </span>
                </div>
                {renderMarkdown(msg.content)}
                {msg.role === 'assistant' && msg.tokens && (
                  <div className="ai-chat-msg-meta">
                    <span>⚡ {msg.latency}s</span>
                    <span>📊 {msg.tokens} tokens</span>
                  </div>
                )}
              </div>
            </div>
          ))}

          {isTyping && (
            <div className="ai-chat-msg assistant">
              <div className="ai-chat-msg-avatar">{modelInfo?.icon || '🤖'}</div>
              <div className="ai-chat-msg-body">
                <div className="ai-typing-indicator">
                  <span></span><span></span><span></span>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        <div className="ai-chat-input-area">
          <div className="ai-chat-input-container">
            <textarea
              ref={inputRef}
              className="ai-chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={`Message ${modelInfo?.name || 'AI'}...`}
              rows={1}
            />
            <button
              className={`ai-chat-send ${input.trim() ? 'active' : ''}`}
              onClick={handleSend}
              disabled={!input.trim()}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="22" y1="2" x2="11" y2="13" />
                <polygon points="22 2 15 22 11 13 2 9 22 2" />
              </svg>
            </button>
          </div>
          <span className="ai-chat-disclaimer">AI can make mistakes. Verify important information.</span>
        </div>
      </div>
    </div>
  );
}

export default AIChat;
